import { Location, TileSize } from "../../models"
import { BODY_GROUND_OFFSET } from "../../lpc/body_placement"
import type { TileMapMetaProvider } from "../../tile_map_meta_provider"
import { withinRoofInterior } from "./roof_interior"
import { stairLandingIsSupported } from "./stairs"

/** Clear a wall beneath a drop with the smallest displacement, preserving its exterior side. */
export const fallLandingNear = (map: TileMapMetaProvider, origin: Location, exterior: boolean): Location | undefined => {
    const interior = exterior ? map.getHousePresentation()?.interiorRects : undefined
    const allowed = (landing: Location) => (!interior || !withinRoofInterior(interior,
        { x: landing.x, y: landing.y + BODY_GROUND_OFFSET })) && stairLandingIsSupported(map, landing)
    if (allowed(origin)) return origin
    const xs = new Set([origin.x]), ys = new Set([origin.y])
    const cellX = Math.floor(origin.x / TileSize), cellY = Math.floor((origin.y + BODY_GROUND_OFFSET) / TileSize)
    // Match the standing body used by landing validation. Test exact wall faces,
    // not just tile centres, so a thin wall cannot send a drop across the room.
    const clearance = 12.51
    for (let x = Math.max(0, cellX - 2); x <= Math.min(map.width() - 1, cellX + 2); x++) {
        for (let y = Math.max(0, cellY - 2); y <= Math.min(map.height() - 1, cellY + 2); y++) {
            xs.add((x + .5) * TileSize)
            ys.add((y + .5) * TileSize - BODY_GROUND_OFFSET)
            const profile = map.getTileBlockingProfile(x, y) ?? (map.isCollisionAt(x, y)
                ? [{ x: 0, y: 0, width: TileSize, height: TileSize }] : [])
            for (const rect of profile) {
                if (rect.groundSupport) continue
                xs.add(x * TileSize + rect.x - clearance)
                xs.add(x * TileSize + rect.x + rect.width + clearance)
                ys.add(y * TileSize + rect.y - clearance)
                ys.add(y * TileSize + rect.y + rect.height + clearance)
            }
        }
    }
    const candidates: Location[] = []
    for (const x of xs) for (const y of ys) candidates.push({ roomId: origin.roomId, x, y })
    const distance = (point: Location) => (point.x - origin.x) ** 2 + (point.y - origin.y) ** 2
    candidates.sort((a, b) => distance(a) - distance(b))
    return candidates.find(allowed)
}
